/**
 * Politicas de RLS - quem ve quais LINHAS dentro de um relatorio.
 *
 * A tela e de consulta e conferencia, nao de ajuste fino: nome, descricao e ativo sao editaveis;
 * relatorio, profile e roles NAO. Nao e limitacao da interface - e o backend
 * (`AccessPolicyUpdate`), e por um bom motivo: trocar a role de uma policy existente muda em
 * silencio o que um grupo inteiro de usuarios enxerga. Para trocar, exclui-se e cria-se outra.
 *
 * Por isso os campos mostram role, identidade e customData: e esta tela que responde "por que
 * fulano esta vendo tudo?" - quase sempre a resposta esta em uma dessas tres. E a quarta resposta
 * e "Ativa": o Viewer descarta policy inativa ANTES de procurar uma aplicavel (`_resolve_role` em
 * `report-viewer/backend/app/services/dashboard_service.py`), entao desativar a unica policy de um
 * relatorio o devolve ao MODO ABERTO. Desativar parece restringir e faz o contrario - dai o aviso
 * em destaque no rodape.
 */

import { StatusBadge } from '@ledger-glass/react'
import { useQuery } from '@tanstack/react-query'

import { fetchPolicies, fetchReports } from '../api/catalog'
import { fetchSnapshot } from '../api/onboarding'
import { RESOURCE_POLICIES } from '../api/types'
import type { AccessPolicy, Report } from '../api/types'
import { fetchMe } from '../auth/api'
import { usePermissoes } from '../auth/permissoes'
import { Alert } from '../components/Alert'
import type { CampoMeta } from '../listagem/campos'
import { IdCurto, Listagem, Marca } from '../listagem/Listagem'
import { Resultado } from '../listagem/Resultado'

const FONTES: Record<string, string> = {
  email: 'e-mail do usuário',
  user_id: 'user_id do Auth',
  organization_id: 'id da organização',
  organization_slug: 'slug da organização',
}

/** Todas as roles da policy: o Power BI aplica o conjunto, nao a primeira (spec 0005). */
function roles(policy: AccessPolicy): string[] {
  return policy.rules.map((regra) => (regra.role_name ?? '').trim()).filter((nome) => nome !== '')
}

function Roles({ policy }: { policy: AccessPolicy }) {
  const nomes = roles(policy)
  // Policy sem role nao filtra nada, e o backend so passou a recusar isso na spec 0005: uma antiga
  // pode estar assim, e em silencio.
  if (nomes.length === 0) return <StatusBadge tone="danger">sem role</StatusBadge>
  return <>{nomes.join(', ')}</>
}

function campos(
  relatorios: Report[],
  profiles: Array<{ id: string; name: string }>,
): Array<CampoMeta<AccessPolicy>> {
  const nomeDoRelatorio = new Map(relatorios.map((item) => [item.id, item.name]))
  const nomeDoProfile = new Map(profiles.map((item) => [item.id, item.name]))

  return [
    { nome: 'name', rotulo: 'Nome da política', grupo: 'Identificação', edicao: { tipo: 'texto' } },
    {
      nome: 'description',
      rotulo: 'Descrição',
      grupo: 'Identificação',
      edicao: { tipo: 'textarea', opcional: true },
    },
    {
      nome: 'report_id',
      rotulo: 'Relatório',
      grupo: 'Vínculo',
      exibir: (item) => nomeDoRelatorio.get(item.report_id) ?? <IdCurto valor={item.report_id} />,
      nota: 'Não é editável: para trocar, exclua esta política e crie outra no onboarding.',
    },
    {
      nome: 'auth_profile_id',
      rotulo: 'Profile (Auth)',
      grupo: 'Vínculo',
      exibir: (item) =>
        nomeDoProfile.get(item.auth_profile_id) ?? <IdCurto valor={item.auth_profile_id} />,
      nota: 'Quem recebe este recorte de linhas.',
    },
    {
      nome: 'dataset_id',
      rotulo: 'Dataset alvo',
      grupo: 'Vínculo',
      formato: 'identificador',
      nota: 'Tem de ser o mesmo dataset do relatório, senão o Viewer nega por incompatibilidade.',
    },
    {
      nome: 'rules',
      rotulo: 'Role(s) de RLS',
      grupo: 'Regra',
      exibir: (item) => <Roles policy={item} />,
      nota: 'Nome exato das roles no semantic model. Várias são aplicadas juntas.',
    },
    {
      nome: 'identity_source',
      rotulo: 'Identidade enviada',
      grupo: 'Regra',
      exibir: (item) => FONTES[item.identity_source ?? ''] ?? item.identity_source ?? '—',
      nota: 'Vai como username no token e é o que a role DAX compara com USERNAME().',
    },
    {
      nome: 'custom_data_source',
      rotulo: 'customData',
      grupo: 'Regra',
      exibir: (item) =>
        item.custom_data_source === null ? (
          <span className="vazio">— não enviar —</span>
        ) : (
          (FONTES[item.custom_data_source] ?? item.custom_data_source)
        ),
      nota: 'Valor extra lido por CUSTOMDATA() no DAX, quando o modelo usa.',
    },
    { nome: 'policy_type', rotulo: 'Tipo', grupo: 'Regra', formato: 'codigo' },
    {
      nome: 'is_active',
      rotulo: 'Política ativa',
      grupo: 'Estado',
      formato: 'booleano',
      nota: 'Desativar ABRE o relatório: sem política ativa, o Viewer volta ao modo aberto.',
      edicao: { tipo: 'booleano' },
    },
    { nome: 'client_slug', rotulo: 'Cliente', grupo: 'Registro', formato: 'codigo' },
    {
      nome: 'client_id',
      rotulo: 'Cliente (identificador)',
      grupo: 'Registro',
      formato: 'identificador',
    },
    { nome: 'id', rotulo: 'Identificador no catálogo', grupo: 'Registro', formato: 'identificador' },
  ]
}

export function PoliticasPage() {
  const { pode } = usePermissoes()
  const politicas = useQuery({ queryKey: ['access-policies'], queryFn: fetchPolicies })
  const relatorios = useQuery({ queryKey: ['reports'], queryFn: fetchReports })
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe })
  const tenantId = me.data?.tenant_id ?? ''
  const snapshot = useQuery({
    queryKey: ['snapshot', tenantId],
    queryFn: () => fetchSnapshot(tenantId),
    enabled: tenantId !== '',
    staleTime: 30 * 60 * 1000,
  })

  const listaDeRelatorios = relatorios.data ?? []
  const listaDeProfiles = (snapshot.data?.profiles ?? []).map((item) => ({
    id: item.id,
    name: item.name,
  }))
  const nomeDoRelatorio = new Map(listaDeRelatorios.map((item) => [item.id, item.name]))
  const nomeDoProfile = new Map(listaDeProfiles.map((item) => [item.id, item.name]))

  return (
    <>

      <Resultado consulta={politicas}>
        {(lista) => (
          <Listagem<AccessPolicy>
            itens={lista}
            chave="access-policies"
            entidade="access-policies"
            rotulo="política"
            rotuloPlural="políticas de RLS"
            campos={campos(listaDeRelatorios, listaDeProfiles)}
            podeGerenciar={pode(RESOURCE_POLICIES, 'manage')}
            idDe={(item) => item.id}
            nomeDe={(item) => item.name}
            verificaDependencias={false}
            colunas={[
              { titulo: 'Política', valor: (item) => item.name },
              {
                titulo: 'Relatório',
                valor: (item) =>
                  nomeDoRelatorio.get(item.report_id) ?? <IdCurto valor={item.report_id} />,
              },
              {
                titulo: 'Profile',
                valor: (item) =>
                  nomeDoProfile.get(item.auth_profile_id) ?? (
                    <IdCurto valor={item.auth_profile_id} />
                  ),
              },
              { titulo: 'Role(s) de RLS', valor: (item) => <Roles policy={item} /> },
              {
                titulo: 'Identidade',
                valor: (item) => FONTES[item.identity_source ?? ''] ?? item.identity_source ?? '—',
              },
              { titulo: 'Ativa', valor: (item) => <Marca valor={item.is_active} /> },
            ]}
            avisoDeEdicao="Relatório, profile e roles não são editáveis: trocá-los mudaria em silêncio o que um grupo inteiro enxerga. Para trocar, exclua esta política e crie outra no onboarding, passo 6."
            vazio="Nenhuma política de RLS. Enquanto não houver, os relatórios ficam em modo aberto para quem alcança o workspace."
          />
        )}
      </Resultado>

      {politicas.data?.length === 0 ? null : (
        <Alert tone="warning">
          <strong>Desativar abre o relatório, não fecha.</strong> O Viewer descarta as políticas
          inativas antes de procurar uma aplicável: se não sobrar nenhuma ativa para o relatório, ele
          volta ao modo aberto e quem alcança o workspace passa a ver todas as linhas. A negativa só
          acontece quando existe política ativa e nenhuma casa com o profile do usuário.
        </Alert>
      )}
    </>
  )
}
